import type { ReactNode } from 'react';
import { FileText, MessageSquarePlus, Truck } from 'lucide-react';
import {
  CheckBoxIcon,
  CoCReplyIcon,
  RightArrowIcon,
  WarningIcon,
} from '@/assets/icons/icons';
import { DelayEventKind } from './delayUntilEventModel';
import styles from './DelayUntilEvent.module.css';

/**
 * Each event's icon, drawn at the size the design uses inside its 15px slot:
 * the Material icons come from a 24px grid scaled to 18.667px, so the
 * glyph-tight check box is sized to the same visible glyph.
 */
const EVENT_ICONS: Record<DelayEventKind, ReactNode> = {
  [DelayEventKind.ALERT_TRIGGERED]: (
    <WarningIcon width={18.667} height={18.667} fill='var(--text-primary)' />
  ),
  [DelayEventKind.ALERT_ACTION_TAKEN]: (
    <CheckBoxIcon width={14} height={14} fill='var(--text-primary)' />
  ),
  // The design gives Snooze ended the same check box as Alert action taken.
  [DelayEventKind.SNOOZE_ENDED]: (
    <CheckBoxIcon width={14} height={14} fill='var(--text-primary)' />
  ),
  [DelayEventKind.STATUS_PROGRESS_UPDATE]: (
    // The design draws an outlined truck. The product's own truck icons are all solid, so this is
    // lucide's outlined one, which the app already depends on.
    <Truck
      size={16}
      strokeWidth={1.75}
      color='var(--text-primary)'
      aria-hidden
    />
  ),
  // The product's reply arrow is only drawn on a grey 28px tile. The CSS class
  // hides the tile and scales the arrow up to the design's 14px glyph.
  [DelayEventKind.REPLY_RECEIVED]: <CoCReplyIcon />,
  // The design's description glyph, drawn outlined like the truck above.
  [DelayEventKind.DOCUMENT_ADDED]: (
    <FileText
      size={16}
      strokeWidth={1.75}
      color='var(--text-primary)'
      aria-hidden
    />
  ),
  // The design's add_comment glyph, drawn outlined like the truck above.
  [DelayEventKind.NOTE_ADDED]: (
    <MessageSquarePlus
      size={16}
      strokeWidth={1.75}
      color='var(--text-primary)'
      aria-hidden
    />
  ),
};

interface DelayEventCardProps {
  kind: DelayEventKind | undefined;
  title: string;
  subtitle: ReactNode;
  onClick: () => void;
  hasError?: boolean;
  ariaLabel?: string;
}

/** A clickable card: the event's icon, its name, a grey line under it, and a chevron. */
export const DelayEventCard = ({
  kind,
  title,
  subtitle,
  onClick,
  hasError = false,
  ariaLabel,
}: DelayEventCardProps) => (
  <button
    type='button'
    className={`${styles.eventCard} ${hasError ? styles.eventCardError : ''}`}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    {kind && (
      <span
        className={`${styles.eventCardIcon} ${
          kind === DelayEventKind.REPLY_RECEIVED
            ? styles.eventCardIconReply
            : ''
        }`}
        data-testid={`event-card-icon-${kind}`}
      >
        {EVENT_ICONS[kind]}
      </span>
    )}
    <span className={styles.eventCardText}>
      <span className={styles.eventCardTitle}>{title}</span>
      <span className={styles.eventCardSubtitle}>{subtitle}</span>
    </span>
    <span className={styles.eventCardChevron}>
      <RightArrowIcon width={16} height={16} />
    </span>
  </button>
);
